import { useCallback, useEffect } from 'react';
// Data Layer
import { RequestIndicatorTypes } from 'data-layer-cr/dist/models';
// Network
import { useActivePdfDownloader } from 'data-layer-cr/dist/network';
import PdfClient from 'src/services/PdfClient';

type Props = {
  urlSalt: number | string;
  productId: number | string;
  policyId: number | string;
  operationNumber: number | string;
};

type ReturnType = {
  requestStatus: RequestIndicatorTypes;
  onRequestDownloadPdf: () => void;
};

export default function useDownloadPolicy({
  urlSalt,
  productId,
  policyId,
  operationNumber,
}: Props): ReturnType {
  /**
   * Pdf downloader logic
   */
  const { requestPDfDownload, requestStatus, pdfFile } = useActivePdfDownloader(
    {
      requestParameters: {
        method: 'GET',
        url: `policyEmission/download/${urlSalt}/?productId=${productId}&policyId=${policyId}&operationId=${operationNumber}`,
        client: PdfClient,
      },
    },
  );

  /**
   * This is the logic that will actually download the pdf through the user's browser
   */
  const downloadPdf = useCallback(() => {
    const pdfUrl = URL.createObjectURL(pdfFile as Blob);
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.setAttribute('target', '_blank');
    link.setAttribute('download', 'poliza.pdf');
    document.body.appendChild(link);
    link.click();
  }, [pdfFile]);

  /**
   * This is the method that will be called on button click. It will either request
   * the pdf from the backend, or download an already requested blob file.
   */
  const onRequestDownloadPdf = useCallback(() => {
    if (typeof pdfFile !== 'undefined') {
      downloadPdf();
    } else {
      requestPDfDownload();
    }
  }, [pdfFile, requestPDfDownload, downloadPdf]);

  /**
   * Auto download the pdf file when we detect a valid blob
   */
  useEffect(() => {
    if (typeof pdfFile !== 'undefined') {
      downloadPdf();
    }
  }, [pdfFile, downloadPdf]);

  return {
    onRequestDownloadPdf,
    requestStatus,
  };
}
